import { type PropsWithChildren, type ReactNode } from "react";
import BaseModule from "@/components/site-builder/BaseModule";
import ColumnCarousel from "@/components/site-builder/ColumnCarousel";
import ColumnGrid from "@/components/site-builder/ColumnGrid";
import { SiteBuilderMultiColumnModuleDisplayType } from "@/constants/site-builder";
import type { BaseMultiColumnSiteBuilderModule } from "@/types/site-builder";

export interface MultiColumnModuleProps<Block, Module> {
  module: Module;
  renderBlock: (block: Block, index: number) => ReactNode;
  blocks?: Block[];
}

type ExtractBlockType<M> =
  M extends BaseMultiColumnSiteBuilderModule<infer B> ? B : never;

export default function MultiColumnModule<
  Module extends BaseMultiColumnSiteBuilderModule<Block>,
  Block = ExtractBlockType<Module>,
>(props: PropsWithChildren<MultiColumnModuleProps<Block, Module>>) {
  const { module, renderBlock, children, blocks: customBlocks = [] } = props;
  const {
    style: {
      display: {
        displayType,
        columnAlignment,
        layoutSize,
        contentLayout,
        displayOrder,
        rotation,
      },
    },
    body: { blocks: moduleBlocks = [] },
  } = module;

  const blocks = customBlocks.length > 0 ? customBlocks : moduleBlocks;

  return (
    <BaseModule module={module}>
      {displayType === SiteBuilderMultiColumnModuleDisplayType.Standard && (
        <ColumnGrid
          items={blocks}
          renderItem={renderBlock}
          contentLayout={contentLayout}
          columnAlignment={columnAlignment}
        />
      )}
      {displayType === SiteBuilderMultiColumnModuleDisplayType.Carousel && (
        <ColumnCarousel
          items={blocks}
          renderItem={renderBlock}
          contentLayout={contentLayout}
          layoutSize={layoutSize}
          rotation={rotation}
          displayOrder={displayOrder}
        />
      )}
      {children}
    </BaseModule>
  );
}
